/**
 * document-meta.ts — runtime SPA document metadata (title, meta tags).
 *
 * SPA is client-rendered; index.html ships static <title>/<meta> with overseas
 * defaults. For CN deployments we rewrite them at runtime based on IS_CN so the
 * browser tab, social crawlers, etc. see the localized brand.
 *
 * (Next-intl/generateMetadata is not available here — this is a Vite SPA.)
 */

import { getBrand } from './brand';
import type { RoomTabIndicator } from '@/components/KnoxChat/room-tab-indicator';

const TITLE_BY_BRAND: Record<string, string> = {
  Knox: 'Knox',
  Ontox: 'Ontox',
};

const DESCRIPTION_BY_BRAND: Record<string, string> = {
  Knox:
    'Knox puts an AI-powered SRE agent team in your workspace. Multi-agent collaboration for monitoring, investigation, change management, and incident resolution across your production environment.',
  Ontox:
    'Ontox 将 AI 驱动的 SRE 智能体团队引入您的工作空间。多智能体协作，覆盖监控、调查、变更管理和事件解决，保障生产环境稳定运行。',
};

const OG_TITLE_BY_BRAND: Record<string, string> = {
  Knox: 'Knox — Your AI SRE Team for Production Operations',
  Ontox: 'Ontox — 生产级智能运维团队',
};

const INDICATOR_COLOR: Record<Exclude<RoomTabIndicator, 'none' | 'working'>, string> = {
  waiting: '#F59E0B',
  completed: '#22C55E',
};
const WORKING_COLOR = '#3B82F6';
const GITLAB_RUNNING_PATH = 'M11,4.71428571 C14.4571429,4.71428571 17.2857143,7.54285714 17.2857143,11 C17.2857143,14.4571429 14.4571429,17.2857143 11,17.2857143 C8.95714286,17.2857143 7.07142857,16.1857143 5.81428571,14.6142857 L11,11 L11,4.71428571';
const faviconDataCache = new Map<string, string>();
let faviconUpdateRevision = 0;

/** Apply brand-aware document metadata. Call once at app bootstrap. */
export function applyDocumentMeta(): void {
  const brand = getBrand();
  const title = TITLE_BY_BRAND[brand] ?? TITLE_BY_BRAND.Knox;
  const description = DESCRIPTION_BY_BRAND[brand] ?? DESCRIPTION_BY_BRAND.Knox;
  const ogTitle = OG_TITLE_BY_BRAND[brand] ?? OG_TITLE_BY_BRAND.Knox;

  if (document.title !== title) {
    document.title = title;
  }

  setMeta('meta[name="description"]', 'content', description);
  setMeta('meta[property="og:title"]', 'content', ogTitle);
  setMeta('meta[property="og:description"]', 'content', description);
  setMeta('meta[property="og:site_name"]', 'content', brand);
  setMeta('meta[name="twitter:title"]', 'content', ogTitle);
  setMeta('meta[name="twitter:description"]', 'content', description);

  // Favicon: index.html 默认 /favicon.png（海外 knox）。CN 切到 /favicon-ontox.svg，
  // 否则浏览器标签页还显示 knox 图标（document title 改了但 favicon 不改）。
  if (brand === 'Ontox') {
    const iconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (iconLink) {
      iconLink.type = 'image/svg+xml';
      iconLink.href = '/favicon-ontox.svg';
    }
  }
}

export async function setFaviconIndicator(indicator: RoomTabIndicator): Promise<void> {
  const revision = ++faviconUpdateRevision;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  const brand = getBrand();
  const brandHref = brand === 'Ontox' ? '/favicon-ontox.svg' : '/favicon.png';
  if (indicator === 'none') {
    link.href = brandHref;
    link.type = brand === 'Ontox' ? 'image/svg+xml' : 'image/png';
    return;
  }
  try {
    const brandDataUrl = await loadFaviconDataUrl(brandHref);
    if (revision !== faviconUpdateRevision) return;
    const indicatorSvg = indicator === 'working'
      ? `<g transform="translate(13 13) scale(.91)"><circle cx="11" cy="11" r="9" fill="white" stroke="${WORKING_COLOR}" stroke-width="1.5"/><path d="${GITLAB_RUNNING_PATH}" fill="${WORKING_COLOR}"/></g>`
      : `<circle cx="23.5" cy="23.5" r="7.5" fill="${INDICATOR_COLOR[indicator]}" stroke="white" stroke-width="2"/>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><image href="${brandDataUrl}" width="32" height="32"/>${indicatorSvg}</svg>`;
    link.type = 'image/svg+xml';
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  } catch {
    if (revision !== faviconUpdateRevision) return;
    link.href = brandHref;
    link.type = brand === 'Ontox' ? 'image/svg+xml' : 'image/png';
  }
}

async function loadFaviconDataUrl(href: string): Promise<string> {
  const cached = faviconDataCache.get(href);
  if (cached) return cached;
  const response = await fetch(new URL(href, document.baseURI));
  if (!response.ok) throw new Error(`failed to load favicon: ${response.status}`);
  const mimeType = response.headers.get('content-type')?.split(';')[0]
    ?? (href.endsWith('.svg') ? 'image/svg+xml' : 'image/png');
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const dataUrl = `data:${mimeType};base64,${btoa(binary)}`;
  faviconDataCache.set(href, dataUrl);
  return dataUrl;
}

function setMeta(selector: string, attr: string, value: string): void {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute(attr, value);
  }
}
